import gsap, { reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";
import { handleEditor } from "@/webflow/detect-editor";
import { onMount, onDestroy } from "@/modules/_";

const TRACK = ".nav-banner_content";

/**
 * Infinite horizontal marquee for `.nav-banner` announcement strips.
 *
 * Webflow: put `data-module="nav-banner-marquee"` on `.nav-banner`.
 * Duplicate items in Designer so the row reads well; the module clones the row once for a seamless loop unless `data-marquee-clone="false"` (you already duplicated the full set twice).
 *
 * `data-marquee-speed` — scroll speed in px/s (default 50).
 * Props helper: `@/utils/nav-banner-marquee-props` → `navBannerMarqueeDataAttrs()`.
 */
export default function (element: HTMLElement, dataset: DOMStringMap) {
  const found = element.querySelector(TRACK);
  if (!(found instanceof HTMLElement) || found.children.length === 0) return;
  const trackEl = found;

  let tween: gsap.core.Tween | null = null;
  let removeResize: (() => void) | null = null;
  let didClone = false;
  let originalChildCount = 0;

  const speedPxPerSec = Math.max(parseFloat(dataset.marqueeSpeed || "50") || 50, 1);

  function teardown() {
    tween?.kill();
    tween = null;
    removeResize?.();
    removeResize = null;
    gsap.set(trackEl, { clearProps: "transform" });
    element.classList.remove("nav-banner_marquee--css");

    if (didClone && originalChildCount > 0) {
      while (trackEl.children.length > originalChildCount) {
        trackEl.removeChild(trackEl.lastChild!);
      }
    }
    didClone = false;
    originalChildCount = 0;
  }

  function applyMotion() {
    const segment = trackEl.scrollWidth / 2;
    if (!Number.isFinite(segment) || segment <= 1) return;

    const duration = Math.max(segment / speedPxPerSec, 0.01);
    element.style.setProperty("--marquee-duration", `${duration}s`);

    tween?.kill();
    tween = null;

    if (reduced) {
      element.classList.add("nav-banner_marquee--css");
      gsap.set(trackEl, { x: 0 });
      return;
    }

    element.classList.remove("nav-banner_marquee--css");
    gsap.set(trackEl, { x: 0 });
    tween = gsap.to(trackEl, {
      x: -segment,
      duration,
      ease: "none",
      repeat: -1,
    });
  }

  function setup() {
    teardown();

    const skipClone = dataset.marqueeClone === "false";
    if (!skipClone) {
      originalChildCount = trackEl.children.length;
      const nodes = Array.from(trackEl.children);
      for (const node of nodes) {
        trackEl.appendChild(node.cloneNode(true));
      }
      didClone = true;
    }

    applyMotion();

    removeResize = Resize.add(() => {
      applyMotion();
    });
  }

  onMount(() => {
    handleEditor((isEditor) => {
      element.classList.toggle("nav-banner_marquee--editor", isEditor);
      if (isEditor) {
        teardown();
        return;
      }
      setup();
    });
  });

  onDestroy(() => {
    teardown();
    element.classList.remove("nav-banner_marquee--editor");
  });
}
